import { Platform } from 'react-native';
import { getApiUrl, formatApiFetchError } from './apiConfig';
import { getSupabaseClient } from './supabase';

const PRODUCTION_RESET_PATH = 'https://www.mingchuankungfu.com/reset-password';

/** Redirect URL Supabase uses after the user clicks the email link. */
export function getPasswordResetRedirectUrl() {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}/reset-password`;
  }
  return PRODUCTION_RESET_PATH;
}

export async function requestPasswordReset(email) {
  const API_URL = getApiUrl();
  const response = await fetch(`${API_URL}/api/users/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: String(email || '').trim() }),
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    throw new Error(data.error || `Request failed (${response.status})`);
  }

  return data;
}

/**
 * Establish recovery session from email link (hash or ?code= on web).
 * @returns {Promise<boolean>} true if a recovery session is ready
 */
export async function preparePasswordRecoverySession() {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) throw error;
    }
  }

  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return Boolean(session);
}

export async function updatePasswordAfterRecovery(newPassword) {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }

  const { data: updateData, error: updateError } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (updateError) throw updateError;

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  if (!session?.access_token) {
    throw new Error('Password updated but session is missing. Try signing in with your new password.');
  }

  const API_URL = getApiUrl();
  const syncResponse = await fetch(`${API_URL}/api/users/complete-password-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ password: newPassword }),
  });

  let syncData = {};
  try {
    syncData = await syncResponse.json();
  } catch {
    syncData = {};
  }

  if (!syncResponse.ok) {
    throw new Error(syncData.error || 'Could not sync password with the app login');
  }

  await supabase.auth.signOut();

  return { user: updateData?.user, message: syncData.message };
}

export function formatPasswordResetError(error) {
  return formatApiFetchError(error, getApiUrl());
}
